import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const refs = useRef({});

  const load = () => api.get("/banners").then(({ data }) => {
    setBanners(data);
    const d = {};
    data.forEach((b) => (d[b.id] = { ...b }));
    setDrafts(d);
  });
  useEffect(() => { load(); }, []);

  const upload = async (id, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], image: fileUrl(data.url) } }));
      toast.success("Image uploaded — remember to save");
    } catch { toast.error("Upload failed"); } finally { setUploadingId(null); }
  };

  const save = async (id) => {
    const b = drafts[id];
    await api.put(`/admin/banners/${id}`, { title: b.title, subtitle: b.subtitle, image: b.image, cta: b.cta || "", link: b.link || "" });
    toast.success("Banner saved");
    load();
  };

  const set = (id, field, value) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  const inputCls = "w-full bg-transparent border border-[var(--line)] px-3 py-2.5 outline-none text-[var(--bronze)] focus:border-[var(--olive)] transition-colors";

  return (
    <div data-testid="admin-banners">
      <h1 className="font-serif text-4xl text-[var(--bronze)] mb-2">Site Banners</h1>
      <p className="text-[var(--olive)] mb-8">Manage the homepage hero and featured banners.</p>

      <div className="space-y-8">
        {banners.map((b) => {
          const d = drafts[b.id] || b;
          return (
            <div key={b.id} data-testid={`banner-${b.id}`} className="border border-[var(--line)] p-6 grid lg:grid-cols-2 gap-6">
              <div>
                <div className="uplabel text-[0.6rem] mb-2">Preview · {b.key}</div>
                <div className="relative aspect-[16/9] overflow-hidden bg-[#F3EECB]">
                  <img src={d.image} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#2b2415]/40 flex flex-col justify-center px-6">
                    <div className="font-serif text-2xl text-[#FBF7DE] whitespace-pre-line leading-tight">{d.title}</div>
                    <div className="text-sm text-[#FBF7DE]/80 mt-1">{d.subtitle}</div>
                  </div>
                </div>
                <input ref={(el) => (refs.current[b.id] = el)} type="file" accept="image/*" className="hidden" id={`banner-up-${b.id}`} onChange={(e) => upload(b.id, e)} data-testid={`banner-upload-${b.id}`} />
                <label htmlFor={`banner-up-${b.id}`} className="flex items-center gap-2 border border-[var(--line)] px-4 py-2 text-sm cursor-pointer text-[var(--bronze)] hover:border-[var(--olive)] transition-colors w-fit mt-3">
                  <Upload className="w-4 h-4" /> {uploadingId === b.id ? "Uploading…" : "Replace Image"}
                </label>
              </div>
              <div className="space-y-3">
                <div><label className="uplabel text-[0.6rem] block mb-1.5">Title</label><textarea rows={2} value={d.title} onChange={(e) => set(b.id, "title", e.target.value)} className={inputCls} /></div>
                <div><label className="uplabel text-[0.6rem] block mb-1.5">Subtitle</label><input value={d.subtitle} onChange={(e) => set(b.id, "subtitle", e.target.value)} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="uplabel text-[0.6rem] block mb-1.5">CTA Text</label><input value={d.cta || ""} onChange={(e) => set(b.id, "cta", e.target.value)} className={inputCls} /></div>
                  <div><label className="uplabel text-[0.6rem] block mb-1.5">CTA Link</label><input value={d.link || ""} onChange={(e) => set(b.id, "link", e.target.value)} className={inputCls} /></div>
                </div>
                <button onClick={() => save(b.id)} data-testid={`save-banner-${b.id}`} className="btn-primary uplabel px-6 py-3">Save Banner</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
